import React, { useState, useEffect } from "react";
import {
  FaStar,
  FaTrophy,
  FaClock,
  FaCheckCircle,
  FaTimesCircle,
  FaSpinner,
  FaChartBar,
  FaColumns,
} from "react-icons/fa";
import axiosInstance from "../config/axiosInstance";
import { API } from "../config/config";
import SortableTh from "./SortableTh";
import UserRideStatsCards, {
  emptyUserStatusBreakdown,
  USER_CARD_FILTER_LABELS,
} from "./UserRideStatsCards";

const normalizeRideStatus = (status) => {
  const s = String(status ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
  if (s === "canceled" || s === "cancelled") return "cancelled";
  if (s === "complete" || s === "completed") return "completed";
  return s;
};

const isUserRideActiveOrCompleted = (ride) => {
  const normalized = normalizeRideStatus(ride?.status);
  return ["completed", "accepted", "arrived", "started"].includes(normalized);
};

const formatLifecycleStatusLabel = (ride) => {
  const raw = String(ride?.status ?? "").trim();
  if (raw) return raw;
  const normalized = normalizeRideStatus(ride?.status);
  if (!normalized) return "N/A";
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const hasAnyDriverAcceptedAction = (ride) =>
  (ride?.driverActions || []).some((a) => a.action === "Accepted");

const hasAnyDriverCancelAction = (ride) =>
  (ride?.driverActions || []).some((a) => a.action === "Cancelled");

const isUserCancelledBeforeAssign = (ride) => {
  if (normalizeRideStatus(ride?.status) !== "cancelled") return false;
  if (ride?.cancelledByUserBeforeAssigned) return true;
  if (String(ride?.cancelledBy ?? "").trim().toLowerCase() !== "user") {
    return false;
  }
  return !hasAnyDriverAcceptedAction(ride);
};

const isUserCancelledAfterAssign = (ride) => {
  if (normalizeRideStatus(ride?.status) !== "cancelled") return false;
  if (ride?.cancelledByUserAfterAccept) return true;
  if (String(ride?.cancelledBy ?? "").trim().toLowerCase() !== "user") {
    return false;
  }
  return hasAnyDriverAcceptedAction(ride);
};

const isDriverCancelledForUser = (ride) => {
  if (isUserRideActiveOrCompleted(ride)) return false;
  const normalized = normalizeRideStatus(ride?.status);
  if (ride?.cancelledByDriverViaActions || ride?.driverCancelledAfterAccept) {
    return true;
  }
  if (normalized === "cancelled") {
    return String(ride?.cancelledBy ?? "").trim().toLowerCase() === "driver";
  }
  if (normalized === "requested" || normalized === "planned") {
    return (
      hasAnyDriverAcceptedAction(ride) &&
      hasAnyDriverCancelAction(ride) &&
      String(ride?.cancelledBy ?? "").trim().toLowerCase() !== "user"
    );
  }
  return false;
};

const getRideStatusLabelForUserAnalytics = (ride) => {
  const normalized = normalizeRideStatus(ride?.status);

  if (isUserRideActiveOrCompleted(ride)) {
    return formatLifecycleStatusLabel(ride);
  }
  if (normalized === "requested" || normalized === "planned") {
    return formatLifecycleStatusLabel(ride);
  }

  if (isUserCancelledBeforeAssign(ride)) {
    return "Cancelled by User - Before Driver Assigned";
  }
  if (isDriverCancelledForUser(ride)) {
    return "Cancelled by Driver";
  }
  if (isUserCancelledAfterAssign(ride)) {
    return "Cancelled by User";
  }
  if (normalized === "cancelled") {
    const by = String(ride?.cancelledBy ?? "").trim().toLowerCase();
    if (by === "user") return "Cancelled by User";
    if (by === "driver") return "Cancelled by Driver";
    if (by === "system") return "Cancelled by system";
    return "Cancelled";
  }
  return formatLifecycleStatusLabel(ride);
};

const getRideTerminalDateTime = (ride) => {
  if (!ride) return null;
  const status = normalizeRideStatus(ride.status);
  if (status === "completed") {
    const raw =
      ride.completedAt || ride.rideEndTime || ride.updatedAt || null;
    if (!raw) return null;
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (status === "cancelled") {
    const raw = ride.cancelledAt || ride.updatedAt || null;
    if (!raw) return null;
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
};

const getDriverCancelTimestamp = (ride) => {
  const fromApi = ride?.driverCancelledAt;
  if (fromApi) {
    const d = new Date(fromApi);
    if (!Number.isNaN(d.getTime())) return d;
  }
  const entry = (ride?.driverActions || []).find(
    (a) => a.action === "Cancelled"
  );
  if (!entry?.timestamp) return null;
  const d = new Date(entry.timestamp);
  return Number.isNaN(d.getTime()) ? null : d;
};

const getRideTerminalOrDriverCancelTime = (ride) => {
  if (isUserRideActiveOrCompleted(ride)) {
    return getRideTerminalDateTime(ride);
  }
  return getRideTerminalDateTime(ride) || getDriverCancelTimestamp(ride);
};

/** Driver's rating of the customer (RideRating doc or ride.driverRating). */
const getDriverToUserRatingDisplay = (ride) => {
  const fromDoc = ride?.driverRideRating?.rating;
  if (fromDoc !== null && fromDoc !== undefined && fromDoc !== "") {
    const n = typeof fromDoc === "number" ? fromDoc : Number(fromDoc);
    if (Number.isFinite(n)) return n;
  }
  const fromEmbedded = ride?.driverRating?.value;
  if (fromEmbedded !== null && fromEmbedded !== undefined && fromEmbedded !== "") {
    const n =
      typeof fromEmbedded === "number" ? fromEmbedded : Number(fromEmbedded);
    if (Number.isFinite(n)) return n;
  }
  return null;
};

const getDriverToUserReviewDisplay = (ride) => {
  const rv = ride?.driverRideRating?.review;
  if (typeof rv === "string" && rv.trim()) return rv.trim();
  const fb = ride?.driverRating?.feedback;
  if (typeof fb === "string" && fb.trim()) return fb.trim();
  return "";
};

const deriveEngagementScore = (completionRate, totalRides, completedRides) => {
  const cr = Number(completionRate) || 0;
  const tr = Number(totalRides) || 0;
  const comp = Number(completedRides) || 0;
  return Math.min(
    100,
    Math.round(cr * 0.5 + Math.min(tr, 40) * 1.25 + Math.min(comp, 20) * 0.5)
  );
};

const formatRatingForDisplay = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0.0";
  const truncated = Math.trunc(n * 10) / 10;
  return truncated.toFixed(1);
};

const getRouteText = (ride) => {
  const pickup =
    typeof (ride.pickupLocation || ride.pickup) === "object"
      ? ride.pickupLocation?.address || ride.pickup?.address || "N/A"
      : ride.pickupLocation || ride.pickup || "N/A";
  const dropoff =
    typeof (ride.dropoffLocation || ride.dropoff) === "object"
      ? ride.dropoffLocation?.address || ride.dropoff?.address || "N/A"
      : ride.dropoffLocation || ride.dropoff || "N/A";
  return { pickup, dropoff };
};

const RIDES_PER_PAGE = 10;

const UserRideAnalytics = ({ userId, user }) => {
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ startDate: "", endDate: "" });
  const [filterError, setFilterError] = useState("");
  const [ridesPage, setRidesPage] = useState(1);
  const [ridesPagination, setRidesPagination] = useState(null);
  const [selectedCardFilter, setSelectedCardFilter] = useState(null);
  const [showColumnOptions, setShowColumnOptions] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState({
    driverPhone: true,
    driverName: true,
    finalRoute: true,
    finalFare: true,
    rideStatus: true,
    createdAt: true,
    terminalAt: true,
    rating: true,
    review: true,
  });
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const fetchUserAnalytics = async (opts = {}) => {
    if (!userId) return;

    const { dateFilters, page: pageArg, cardFilter: cardFilterArg } = opts;
    const activeFilters =
      dateFilters !== undefined ? dateFilters : filters;
    const pageToUse =
      pageArg !== undefined ? pageArg : ridesPage;
    const cardFilterToUse =
      cardFilterArg !== undefined ? cardFilterArg : selectedCardFilter;

    try {
      setLoading(true);
      setError(null);
      setFilterError("");

      const params = {
        includeRides: "true",
        page: pageToUse,
        limit: RIDES_PER_PAGE,
      };
      if (activeFilters.startDate) params.dateFrom = activeFilters.startDate;
      if (activeFilters.endDate) params.dateTo = activeFilters.endDate;
      if (cardFilterToUse) params.cardFilter = cardFilterToUse;

      const activeSort = opts.sortConfig ?? sortConfig;
      if (activeSort?.key) {
        params.sortBy = activeSort.key;
        params.sortOrder = activeSort.direction || "asc";
      }

      const response = await axiosInstance.get(
        `${API.USER.USER_ANALYTICS}/${userId}`,
        { params }
      );
      const data = response.data;

      const overview = data.analytics?.overview || {};
      const rates = data.analytics?.rates || {};

      const completionRate = rates.completionRate ?? 0;
      const totalRides = overview.totalRides || 0;
      const completedRides = overview.completedRides || 0;

      const driverToUserRatingsCount =
        overview.driverToUserRatingsCount ??
        overview.driverFeedbackRatingsCount ??
        0;
      const averageDriverToUserRating =
        overview.averageDriverToUserRating != null &&
        !Number.isNaN(Number(overview.averageDriverToUserRating))
          ? Number(overview.averageDriverToUserRating)
          : overview.averageDriverFeedbackRating != null &&
              !Number.isNaN(Number(overview.averageDriverFeedbackRating))
            ? Number(overview.averageDriverFeedbackRating)
            : null;
      const avgRatingGiven = overview.averageRatingGiven;
      const profileTabRating =
        user?.rating != null && user.rating !== "" && !Number.isNaN(Number(user.rating))
          ? Number(user.rating)
          : null;
      const displayRating =
        profileTabRating != null
          ? profileTabRating
          : averageDriverToUserRating != null
            ? averageDriverToUserRating
            : avgRatingGiven != null && !Number.isNaN(Number(avgRatingGiven))
              ? Number(avgRatingGiven)
              : 0;

      const engagementScore = deriveEngagementScore(
        completionRate,
        totalRides,
        completedRides
      );

      setAnalytics({
        basicInfo: {
          rating: displayRating,
          totalRatings: driverToUserRatingsCount,
          engagementScore,
          userSince: user?.createdAt
            ? new Date(user.createdAt).toLocaleDateString()
            : "N/A",
        },
        statusBreakdown:
          data.analytics?.statusBreakdown || emptyUserStatusBreakdown(),
        recentRides: data.recentRides || [],
        daysSinceJoining: data.onboarding?.daysSinceJoining || 0,
      });
      setRidesPagination(data.pagination ?? null);
      setRidesPage(data.pagination?.currentPage ?? pageToUse);
      if (cardFilterArg !== undefined) {
        setSelectedCardFilter(cardFilterArg || null);
      } else if (data.activeCardFilter !== undefined) {
        setSelectedCardFilter(data.activeCardFilter || null);
      }
    } catch (err) {
      console.error("Error fetching user analytics:", err);
      setError("Failed to load user analytics. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setFilters({ startDate: "", endDate: "" });
    setFilterError("");
    setSelectedCardFilter(null);
    setRidesPage(1);
    fetchUserAnalytics({
      dateFilters: { startDate: "", endDate: "" },
      page: 1,
      cardFilter: null,
    });
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCardClick = (cardKey) => {
    const nextFilter =
      !cardKey || selectedCardFilter === cardKey ? null : cardKey;
    setRidesPage(1);
    fetchUserAnalytics({ page: 1, cardFilter: nextFilter });
  };

  const handleClearCardFilter = () => {
    setRidesPage(1);
    fetchUserAnalytics({ page: 1, cardFilter: null });
  };

  const handleFilterChange = (field, value) => {
    setFilterError("");
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleApplyFilters = () => {
    if (!filters.startDate && !filters.endDate) {
      setFilterError("Select at least a start date or an end date.");
      return;
    }
    if (
      filters.startDate &&
      filters.endDate &&
      filters.startDate > filters.endDate
    ) {
      setFilterError("Start date cannot be greater than end date.");
      return;
    }
    setRidesPage(1);
    fetchUserAnalytics({ dateFilters: filters, page: 1 });
  };

  const handleClearFilters = () => {
    const cleared = { startDate: "", endDate: "" };
    setFilters(cleared);
    setFilterError("");
    setRidesPage(1);
    fetchUserAnalytics({ dateFilters: cleared, page: 1 });
  };

  const handleSortSet = (key, direction) => {
    const next = { key, direction };
    setSortConfig(next);
    setRidesPage(1);
    fetchUserAnalytics({ page: 1, sortConfig: next });
  };

  const handleSortLabelClick = (key) => {
    setSortConfig((prev) => {
      const next = {
        key,
        direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
      };
      setRidesPage(1);
      fetchUserAnalytics({ page: 1, sortConfig: next });
      return next;
    });
  };

  const analyticsSortThClass =
    "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100 bg-gray-50";

  const analyticsSortTh = (columnKey, label) => (
    <SortableTh
      columnKey={columnKey}
      label={label}
      activeKey={sortConfig.key}
      activeDirection={sortConfig.direction}
      onSortSet={handleSortSet}
      onSortLabelClick={handleSortLabelClick}
      className={analyticsSortThClass}
    />
  );

  const displayRecentRides = Array.isArray(analytics?.recentRides)
    ? analytics.recentRides
    : [];

  const visibleColumnCount = Object.values(visibleColumns).filter(Boolean).length;

  const getStatusIcon = (ride) => {
    const normalized = normalizeRideStatus(ride?.status);
    if (normalized === "completed") {
      return <FaCheckCircle className="text-green-500" />;
    }
    const label = getRideStatusLabelForUserAnalytics(ride);
    const lower = label.toLowerCase();
    if (lower.includes("cancelled") || lower.includes("canceled")) {
      return <FaTimesCircle className="text-red-500" />;
    }
    if (lower.includes("pending") || lower.includes("requested")) {
      return <FaClock className="text-yellow-500" />;
    }
    return <FaSpinner className="text-gray-500" />;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center py-20">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <FaChartBar className="text-6xl text-purple-500 mb-4 mx-auto" />
          <FaSpinner className="animate-spin text-3xl text-purple-500 mb-4 mx-auto" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            Loading Ride Analytics
          </h2>
          <p className="text-gray-600 mb-4">
            Please wait while we analyze user data...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col justify-center items-center py-12">
        <div className="text-red-500 text-lg mb-4">{error}</div>
        <button
          type="button"
          onClick={() => fetchUserAnalytics()}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="flex justify-center items-center py-12">
        <span className="text-gray-500 text-lg">
          No analytics data available
        </span>
      </div>
    );
  }

  const { basicInfo, statusBreakdown } = analytics;
  const activeFilterLabel =
    USER_CARD_FILTER_LABELS[selectedCardFilter || "totalRequestedRides"] ||
    selectedCardFilter ||
    "Total Requested Rides";

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-purple-500">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">
              {user?.firstName || user?.fullName || "User"} - Ride Analytics
            </h2>
            <div className="flex items-center space-x-4 mt-2 text-sm text-gray-600">
              <span>User ID: {userId}</span>
              <span>
                Status:{" "}
                <span
                  className={`font-semibold ${
                    user?.status === "Active" || user?.status === "active"
                      ? "text-green-600"
                      : user?.status === "Verified"
                        ? "text-blue-600"
                        : "text-yellow-600"
                  }`}
                >
                  {user?.status || "Unknown"}
                </span>
              </span>
              <span>Member Age: {analytics?.daysSinceJoining} days</span>
              <span>Member since: {basicInfo.userSince}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-4">
        <p className="text-sm text-gray-600 mb-3">
          Total Requested Rides is the combined unique count and trip amount from
          all status cards below. Click Total Requested Rides (or load the page) to
          see the full list; click any other card to filter the table.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange("startDate", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange("endDate", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <div>
            <button
              type="button"
              onClick={handleApplyFilters}
              className="w-full px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
            >
              Apply
            </button>
          </div>
          <div>
            <button
              type="button"
              onClick={handleClearFilters}
              className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
            >
              Clear
            </button>
          </div>
        </div>
        {filterError && (
          <p className="mt-2 text-sm text-red-600">{filterError}</p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-lg p-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-yellow-100 text-xs">Rating</p>
              <p className="text-xl font-bold">
                {formatRatingForDisplay(basicInfo.rating)}
              </p>
              {basicInfo.totalRatings > 0 && (
                <p className="text-yellow-200 text-[10px] mt-0.5">
                  {basicInfo.totalRatings} rating
                  {basicInfo.totalRatings === 1 ? "" : "s"} from drivers
                </p>
              )}
            </div>
            <FaStar className="text-2xl text-yellow-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg p-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-xs">Performance</p>
              <p className="text-xl font-bold">
                {basicInfo.engagementScore}/100
              </p>
            </div>
            <FaTrophy className="text-2xl text-purple-200" />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-4">
        <UserRideStatsCards
          stats={statusBreakdown}
          loading={loading}
          selectedCardFilter={selectedCardFilter}
          onCardClick={handleCardClick}
        />
      </div>

      <div className="bg-white rounded-lg shadow-md">
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-wrap items-center justify-between gap-2 relative">
            <div>
              <h3 className="text-lg font-semibold text-gray-800">Recent Rides</h3>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-purple-50 text-purple-800 text-xs font-medium border border-purple-200">
                  Filter: {activeFilterLabel}
                  {selectedCardFilter ? (
                    <button
                      type="button"
                      onClick={handleClearCardFilter}
                      className="text-purple-600 hover:text-purple-800 font-semibold"
                      aria-label="Clear card filter"
                    >
                      ×
                    </button>
                  ) : null}
                </span>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                Total Requested Rides lists every unique trip from all status cards.
                Card filters narrow the table to one category.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowColumnOptions((v) => !v)}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white hover:bg-gray-50"
            >
              <FaColumns className="mr-2" />
              Columns
            </button>
            {showColumnOptions && (
              <div className="absolute right-0 top-full mt-1 py-2 px-3 bg-white border rounded-lg shadow-lg z-20 min-w-[220px]">
                {[
                  ["driverPhone", "Driver Mobile Number"],
                  ["driverName", "Driver Name"],
                  ["finalRoute", "Final Route"],
                  ["finalFare", "Final Fare"],
                  ["rideStatus", "Ride Status"],
                  ["createdAt", "Created Date & Time"],
                  ["terminalAt", "Completed/Cancelled Date and Time"],
                  ["rating", "Driver Rating"],
                  ["review", "Driver Review"],
                ].map(([col, label]) => (
                  <label
                    key={col}
                    className="flex items-center gap-2 py-1 cursor-pointer text-sm"
                  >
                    <input
                      type="checkbox"
                      checked={visibleColumns[col]}
                      onChange={(e) =>
                        setVisibleColumns((v) => ({
                          ...v,
                          [col]: e.target.checked,
                        }))
                      }
                      className="rounded"
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="overflow-x-auto overflow-y-auto max-h-[70vh]">
          <table className="w-full">
            <thead className="bg-gray-50 sticky top-0 z-20 shadow-sm">
              <tr>
                {analyticsSortTh("rideId", "Ride ID")}
                {visibleColumns.driverPhone &&
                  analyticsSortTh("driverPhone", "Driver Mobile Number")}
                {visibleColumns.driverName &&
                  analyticsSortTh("driverName", "Driver Name")}
                {visibleColumns.finalRoute &&
                  analyticsSortTh("finalRoute", "Final Route")}
                {visibleColumns.finalFare &&
                  analyticsSortTh("finalFare", "Final Fare")}
                {visibleColumns.rideStatus &&
                  analyticsSortTh("rideStatus", "Ride Status")}
                {visibleColumns.createdAt &&
                  analyticsSortTh("createdAt", "Created Date & Time")}
                {visibleColumns.terminalAt &&
                  analyticsSortTh(
                    "terminalAt",
                    "Completed/Cancelled Date and Time"
                  )}
                {visibleColumns.rating &&
                  analyticsSortTh("rating", "Driver Rating")}
                {visibleColumns.review &&
                  analyticsSortTh("review", "Driver Review")}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {displayRecentRides.length > 0 ? (
                displayRecentRides.map((ride, index) => {
                  const statusLabel = getRideStatusLabelForUserAnalytics(ride);
                  const normalizedStatus = normalizeRideStatus(ride?.status);
                  const isCancelledLabel =
                    normalizedStatus === "cancelled" ||
                    (statusLabel.toLowerCase().includes("cancelled") &&
                      !isUserRideActiveOrCompleted(ride));
                  const isCompletedLabel = normalizedStatus === "completed";

                  return (
                    <tr
                      key={ride._id || ride.id || index}
                      className="hover:bg-gray-50"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {ride._id}
                      </td>
                      {visibleColumns.driverPhone && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {ride.driverId?.phone || "N/A"}
                        </td>
                      )}
                      {visibleColumns.driverName && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {ride.driverId?.fullName || "N/A"}
                        </td>
                      )}
                      {visibleColumns.finalRoute && (
                        <td className="px-6 py-4 text-sm text-gray-900">
                          <div>
                            <div className="font-medium">
                              {getRouteText(ride).pickup}
                            </div>
                            <div className="text-gray-500">
                              → {getRouteText(ride).dropoff}
                            </div>
                          </div>
                        </td>
                      )}
                      {visibleColumns.finalFare && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatCurrency(ride.fare || ride.amount)}
                        </td>
                      )}
                      {visibleColumns.rideStatus && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            {getStatusIcon(ride)}
                            <span
                              className={`text-sm font-medium ${
                                isCompletedLabel
                                  ? "text-green-600"
                                  : isCancelledLabel
                                    ? "text-red-600"
                                    : "text-yellow-600"
                              }`}
                            >
                              {statusLabel}
                            </span>
                          </div>
                        </td>
                      )}
                      {visibleColumns.createdAt && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {(() => {
                            const raw = ride?.createdAt;
                            if (!raw) {
                              return <span className="text-gray-400">N/A</span>;
                            }
                            const created = new Date(raw);
                            if (Number.isNaN(created.getTime())) {
                              return <span className="text-gray-400">N/A</span>;
                            }
                            return (
                              <div>
                                <div>
                                  {created.toLocaleDateString("en-GB", {
                                    day: "2-digit",
                                    month: "short",
                                    year: "numeric",
                                  })}
                                </div>
                                <div className="text-xs text-gray-400">
                                  {created.toLocaleTimeString("en-GB", {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })}
                                </div>
                              </div>
                            );
                          })()}
                        </td>
                      )}
                      {visibleColumns.terminalAt && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {(() => {
                            const terminal =
                              getRideTerminalOrDriverCancelTime(ride);
                            if (!terminal) {
                              return <span className="text-gray-400">N/A</span>;
                            }
                            return (
                              <div>
                                <div>
                                  {terminal.toLocaleDateString("en-GB", {
                                    day: "2-digit",
                                    month: "short",
                                    year: "numeric",
                                  })}
                                </div>
                                <div className="text-xs text-gray-400">
                                  {terminal.toLocaleTimeString("en-GB", {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })}
                                </div>
                              </div>
                            );
                          })()}
                        </td>
                      )}
                      {visibleColumns.rating && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {(() => {
                            const r = getDriverToUserRatingDisplay(ride);
                            return r != null ? r : "N/A";
                          })()}
                        </td>
                      )}
                      {visibleColumns.review && (
                        <td className="px-6 py-4 text-sm text-gray-600 max-w-xs">
                          {(() => {
                            const text = getDriverToUserReviewDisplay(ride);
                            if (!text) {
                              return <span className="text-gray-400">N/A</span>;
                            }
                            return (
                              <span className="line-clamp-3" title={text}>
                                {text}
                              </span>
                            );
                          })()}
                        </td>
                      )}
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan={1 + visibleColumnCount}
                    className="px-6 py-4 text-center text-gray-500"
                  >
                    No recent rides found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {ridesPagination && ridesPagination.totalItems > 0 && (
          <div className="px-6 py-4 border-t border-gray-200 flex flex-wrap items-center justify-between gap-4">
            <span className="text-sm text-gray-600">
              Page {ridesPagination.currentPage} of{" "}
              {Math.max(1, ridesPagination.totalPages)} (
              {ridesPagination.totalItems} rides)
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={!ridesPagination.hasPrev}
                onClick={() =>
                  fetchUserAnalytics({
                    page: ridesPagination.currentPage - 1,
                  })
                }
                className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                type="button"
                disabled={!ridesPagination.hasNext}
                onClick={() =>
                  fetchUserAnalytics({
                    page: ridesPagination.currentPage + 1,
                  })
                }
                className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserRideAnalytics;
